import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

// localStorage keys for reload loop tracking
const RELOAD_LAST_TS_KEY = 'reload_loop_last_ts'
const RELOAD_COUNT_KEY = 'reload_loop_count'
const RELOAD_SESSION_ID_KEY = 'reload_loop_session_id'
const RELOAD_MAX_COUNT_KEY = 'reload_loop_max_count' // Track highest count reached

// Single threshold: 10 seconds
const RAPID_RELOAD_THRESHOLD_MS = 10000 // 10 seconds - if reload happens within this, it's rapid

export interface ReloadLoopData {
  rapidReloadCount: number // Number of consecutive rapid reloads (each < 10 seconds apart)
  maxRapidReloadCount: number // Highest rapid reload count reached in this session
  lastTimestamp: number
  timeSinceLastReload: number
  isRapidReload: boolean // Was THIS reload rapid (< 10 seconds since last)?
  isInLoop: boolean // rapidReloadCount >= 3
  sessionId: string
}

/**
 * Generate a unique session ID for tracking reload loops
 */
function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Track and detect infinite reload loops.
 * Call this EARLY in app initialization (before React renders).
 *
 * Returns data about the current reload state that can be used
 * for Sentry context and Firebase tracking.
 */
export function trackReloadLoop(): ReloadLoopData {
  const now = Date.now()

  let lastTimestamp = 0
  let rapidReloadCount = 0
  let maxRapidReloadCount = 0
  let sessionId = ''

  try {
    lastTimestamp = parseInt(localStorage.getItem(RELOAD_LAST_TS_KEY) || '0', 10)
    rapidReloadCount = parseInt(localStorage.getItem(RELOAD_COUNT_KEY) || '0', 10)
    maxRapidReloadCount = parseInt(localStorage.getItem(RELOAD_MAX_COUNT_KEY) || '0', 10)
    sessionId = localStorage.getItem(RELOAD_SESSION_ID_KEY) || ''
  } catch (e) {
    // localStorage unavailable - use defaults
    console.warn('[ReloadLoopTracker] localStorage unavailable:', e)
  }

  const timeSinceLastReload = lastTimestamp > 0 ? now - lastTimestamp : Infinity
  const isRapidReload = timeSinceLastReload < RAPID_RELOAD_THRESHOLD_MS

  // Simple logic: one 10-second window
  // - If reload within 10 seconds: increment count
  // - If reload after 10 seconds: reset count
  if (isRapidReload) {
    // Rapid reload (< 10 seconds) - increment counter
    rapidReloadCount += 1
    maxRapidReloadCount = Math.max(maxRapidReloadCount, rapidReloadCount)
  } else {
    // More than 10 seconds - reset
    rapidReloadCount = 0
    maxRapidReloadCount = 0
    sessionId = generateSessionId()
  }

  const isInLoop = rapidReloadCount >= 3

  // Update localStorage
  try {
    localStorage.setItem(RELOAD_LAST_TS_KEY, now.toString())
    localStorage.setItem(RELOAD_COUNT_KEY, rapidReloadCount.toString())
    localStorage.setItem(RELOAD_MAX_COUNT_KEY, maxRapidReloadCount.toString())
    localStorage.setItem(RELOAD_SESSION_ID_KEY, sessionId)
  } catch (e) {
    console.warn('[ReloadLoopTracker] Failed to update localStorage:', e)
  }

  const data: ReloadLoopData = {
    rapidReloadCount,
    maxRapidReloadCount,
    lastTimestamp: now,
    timeSinceLastReload: timeSinceLastReload === Infinity ? -1 : timeSinceLastReload,
    isRapidReload,
    isInLoop,
    sessionId
  }

  // Set Sentry context immediately
  updateSentryContext(data)

  // If in a loop, capture events in both Sentry and PostHog
  if (isInLoop) {
    Sentry.captureMessage(
      `Reload loop detected: ${rapidReloadCount} rapid reloads within 10s`,
      rapidReloadCount >= 5 ? 'error' : 'warning'
    )

    posthog.capture('reload_loop_detected', {
      rapidReloadCount,
      maxRapidReloadCount,
      loopSessionId: sessionId
    })
  }

  return data
}

/**
 * Update Sentry and PostHog with reload loop data.
 * Call this to enrich all subsequent events in both systems.
 */
export function updateSentryContext(data: ReloadLoopData): void {
  const reloadLoopProps = {
    rapidReloadCount: data.rapidReloadCount,
    maxRapidReloadCount: data.maxRapidReloadCount,
    timeSinceLastReloadMs: data.timeSinceLastReload,
    isRapidReload: data.isRapidReload,
    isInLoop: data.isInLoop,
    loopSessionId: data.sessionId,
    timestamp: new Date().toISOString()
  }

  // Sentry context
  Sentry.setContext('reloadLoop', reloadLoopProps)
  Sentry.setTag('rapidReloadCount', data.rapidReloadCount.toString())
  Sentry.setTag('isInReloadLoop', data.isInLoop.toString())

  // PostHog - register as super properties (sent with every event)
  posthog.register({
    rapidReloadCount: data.rapidReloadCount,
    isInReloadLoop: data.isInLoop
  })
}

/**
 * Get current reload loop data without incrementing counter.
 * Use this when you need to read the data without triggering a new "reload".
 */
export function getReloadLoopData(): ReloadLoopData | null {
  try {
    const lastTimestamp = parseInt(localStorage.getItem(RELOAD_LAST_TS_KEY) || '0', 10)
    const rapidReloadCount = parseInt(localStorage.getItem(RELOAD_COUNT_KEY) || '0', 10)
    const maxRapidReloadCount = parseInt(localStorage.getItem(RELOAD_MAX_COUNT_KEY) || '0', 10)
    const sessionId = localStorage.getItem(RELOAD_SESSION_ID_KEY) || ''

    if (!lastTimestamp) return null

    const now = Date.now()
    const timeSinceLastReload = now - lastTimestamp

    return {
      rapidReloadCount,
      maxRapidReloadCount,
      lastTimestamp,
      timeSinceLastReload,
      isRapidReload: timeSinceLastReload < RAPID_RELOAD_THRESHOLD_MS,
      isInLoop: rapidReloadCount >= 3,
      sessionId
    }
  } catch (e) {
    return null
  }
}

/**
 * Gather browser environment info for RCA
 */
async function getBrowserEnvironment(): Promise<Record<string, unknown>> {
  const env: Record<string, unknown> = {
    userAgent: navigator.userAgent,
    onLine: navigator.onLine,
    cookieEnabled: navigator.cookieEnabled,
    language: navigator.language,
    platform: navigator.platform,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: (navigator as { deviceMemory?: number }).deviceMemory ?? 'unknown',
    url: window.location.href,
    timestamp: new Date().toISOString()
  }

  // Check storage availability
  try {
    localStorage.setItem('__storage_test__', 'test')
    localStorage.removeItem('__storage_test__')
    env.localStorageAvailable = true
  } catch {
    env.localStorageAvailable = false
  }

  try {
    sessionStorage.setItem('__storage_test__', 'test')
    sessionStorage.removeItem('__storage_test__')
    env.sessionStorageAvailable = true
  } catch {
    env.sessionStorageAvailable = false
  }

  // Check IndexedDB availability
  env.indexedDBAvailable = typeof indexedDB !== 'undefined'

  // Check if likely private browsing (storage persists check)
  try {
    const testKey = '__private_test__'
    localStorage.setItem(testKey, 'test')
    const stored = localStorage.getItem(testKey)
    localStorage.removeItem(testKey)
    env.likelyPrivateBrowsing = stored !== 'test'
  } catch {
    env.likelyPrivateBrowsing = true
  }

  // Get storage quota (async)
  if (navigator.storage?.estimate) {
    try {
      const estimate = await navigator.storage.estimate()
      env.storageQuota = estimate.quota
      env.storageUsage = estimate.usage
      env.storageUsagePercent = estimate.quota ? Math.round(((estimate.usage || 0) / estimate.quota) * 100) : 'unknown'
    } catch {
      env.storageQuota = 'error'
    }
  } else {
    env.storageQuota = 'unsupported'
  }

  // Get current storage sizes
  try {
    let localStorageSize = 0
    for (const key in localStorage) {
      if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
        localStorageSize += localStorage.getItem(key)?.length || 0
      }
    }
    env.localStorageSizeBytes = localStorageSize

    let sessionStorageSize = 0
    for (const key in sessionStorage) {
      if (Object.prototype.hasOwnProperty.call(sessionStorage, key)) {
        sessionStorageSize += sessionStorage.getItem(key)?.length || 0
      }
    }
    env.sessionStorageSizeBytes = sessionStorageSize
  } catch {
    env.localStorageSizeBytes = 'error'
    env.sessionStorageSizeBytes = 'error'
  }

  return env
}

/**
 * Report that a reload was blocked (e.g., setSession failed).
 * Captures browser environment for root cause analysis.
 */
export function reportReloadBlocked(reason: string): void {
  const data = getReloadLoopData()
  const count = data?.rapidReloadCount ?? 0

  // Capture environment async and send to Sentry/PostHog
  getBrowserEnvironment().then((env) => {
    Sentry.captureMessage(`Reload BLOCKED: ${reason} (count: ${count})`, {
      level: 'error',
      contexts: {
        browserEnvironment: env
      }
    })

    posthog.capture('reload_blocked', {
      reason,
      rapidReloadCount: count,
      ...env
    })
  })
}

/**
 * Reset the reload loop counter.
 * Call this when a user explicitly clears cache or uses escape hatch.
 */
export function resetReloadLoopCounter(): void {
  try {
    localStorage.removeItem(RELOAD_LAST_TS_KEY)
    localStorage.removeItem(RELOAD_COUNT_KEY)
    localStorage.removeItem(RELOAD_MAX_COUNT_KEY)
    localStorage.removeItem(RELOAD_SESSION_ID_KEY)
  } catch (e) {
    // Ignore errors
  }
}

/**
 * Mark that a reload is about to happen (called before window.location.reload()).
 * Captures the reason in Sentry/PostHog for root cause analysis.
 */
export function markIntentionalReload(reason: string): void {
  try {
    localStorage.setItem('reload_loop_last_reason', reason)
  } catch (e) {
    // Ignore errors
  }

  // Get current count to include in the event
  const data = getReloadLoopData()
  const count = data?.rapidReloadCount ?? 0

  // Send to both Sentry and PostHog with the reason
  Sentry.captureMessage(`Page reload: ${reason} (count: ${count})`, count >= 3 ? 'warning' : 'info')

  posthog.capture('page_reload', {
    reason,
    rapidReloadCount: count,
    isInLoop: count >= 3
  })
}
